<template>
    <section class="section">
        <div class="container">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("audit_logs.page_title") }}</h1>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <div class="field is-grouped mb-3 is-flex-wrap-wrap mb">
                        <div class="control is-expanded">
                            <QueryInput v-model="query" />
                        </div>
                        <div class="control mt-2-mobile">
                            <div class="select is-fullwidth">
                                <select v-model="selectedLogType">
                                    <option :value="null">{{ translate("audit_logs.all_log_types") }}</option>
                                    <option value="paymentUpdated">{{ translateLogType("paymentUpdated") }}</option>
                                    <option value="eventRegistrationUpdate">
                                        {{ translateLogType("eventRegistrationUpdate") }}
                                    </option>
                                    <option value="competitorCallIncreased">
                                        {{ translateLogType("competitorCallIncreased") }}
                                    </option>
                                    <option value="verificationStatusChanged">
                                        {{ translateLogType("verificationStatusChanged") }}
                                    </option>
                                    <option value="fightFinished">
                                        {{ translateLogType("fightFinished") }}
                                    </option>
                                    <option value="fightResultReverted">
                                        {{ translateLogType("fightResultReverted") }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="is-flex is-justify-content-center" v-if="refreshingInProgress">
                        <LoadingSpinner2 />
                    </div>
                    <template v-else>
                        <div class="box is-condensed" v-for="auditLog in auditLogs" :key="auditLog.id">
                            <p>
                                <span class="has-text-weight-bold">{{ translateLogType(auditLog.logType) }}</span>
                                <ActionPerformedByUser :user="auditLog.user" />
                            </p>
                            <p class="has-text-grey-light">
                                <span class="icon">
                                    <i class="fas fa-clock"></i>
                                </span>
                                <span>
                                    {{ autoConvertToTimezone(auditLog.created) }}
                                </span>
                            </p>

                            <PaymentUpdated
                                v-if="auditLog.context.logType === 'paymentUpdated'"
                                :context="auditLog.context"
                                :user="auditLog.user"
                            />
                            <EventRegistrationUpdate
                                v-if="auditLog.context.logType === 'eventRegistrationUpdate'"
                                :context="auditLog.context"
                                :user="auditLog.user"
                            />
                            <CompetitorCallIncreased
                                v-if="auditLog.context.logType === 'competitorCallIncreased'"
                                :context="auditLog.context"
                                :user="auditLog.user"
                            />
                            <VerificationStatusChanged
                                v-if="auditLog.context.logType === 'verificationStatusChanged'"
                                :context="auditLog.context"
                                :user="auditLog.user"
                            />
                            <FightFinished
                                v-if="auditLog.context.logType === 'fightFinished'"
                                :context="auditLog.context"
                                :user="auditLog.user"
                            />
                            <FightResultReverted
                                v-if="auditLog.context.logType === 'fightResultReverted'"
                                :context="auditLog.context"
                                :user="auditLog.user"
                            />
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import { onMounted, ref, watch } from "vue";
    import auditLogsApi, { FullAuditLogEntry, LogType } from "../Api/auditLogsApi";
    import { autoConvertToTimezone } from "../Common/dateTimeUtils";
    import { translate } from "../Common/translations";
    import ActionPerformedByUser from "./AuditLogEvent/ActionPerformedByUser.vue";
    import QueryInput from "../Common/QueryInput.vue";
    import debounce from "lodash.debounce";
    import { handleError } from "../Common/errorHandler";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import PaymentUpdated from "./AuditLogEvent/PaymentUpdated.vue";
    import EventRegistrationUpdate from "./AuditLogEvent/EventRegistrationUpdate.vue";
    import CompetitorCallIncreased from "./AuditLogEvent/CompetitorCallIncreased.vue";
    import VerificationStatusChanged from "./AuditLogEvent/VerificationStatusChanged.vue";
    import FightFinished from "./AuditLogEvent/FightFinished.vue";
    import FightResultReverted from "./AuditLogEvent/FightResultReverted.vue";

    const props = defineProps<{
        eventId: number;
    }>();

    const query = ref<string>("");
    const selectedLogType = ref<LogType | null>(null);
    const auditLogs = ref<FullAuditLogEntry[]>([]);
    const refreshingInProgress = ref<boolean>(false);

    const translateLogType = (logType: LogType): string => {
        return translate(`audit_logs.log_type.${logType}`);
    };

    watch(
        query,
        debounce(() => {
            refreshAuditLogs();
        }, 250),
    );

    watch(selectedLogType, () => {
        refreshAuditLogs();
    });

    function refreshAuditLogs() {
        refreshingInProgress.value = true;
        auditLogsApi
            .getAuditLogsListing(props.eventId, {
                query: query.value,
                offset: 0,
                limit: 50,
                logType: selectedLogType.value,
            })
            .then(response => {
                auditLogs.value = response;
            })
            .catch(handleError)
            .finally(() => {
                refreshingInProgress.value = false;
            });
    }

    onMounted(() => {
        return refreshAuditLogs();
    });
</script>

<style scoped></style>
